import * as React from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import propz from 'propz';
import { getName, searchFunctionTeamSports } from 'Src/helpers/autocomplete/autocomplete';
import { GENDER, SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { AppUser } from 'Src/views/App/App';
import { SelectOption } from 'Src/helpers/table/table';
import { SchoolForm } from 'Src/models/form';
import { SchoolHouse } from 'Src/models/house';
import { getSelectOptionForAge } from 'Src/helpers/table/select';
import { checkAvailabilityTeamName } from 'Src/helpers/service/admin/schoolTeams';
import { PeopleFilterChipSelect } from 'Src/views/GenericView/AdminView/Users/PeopleFilterChipSelect';
import { useSimpleModalRequestClose } from 'Src/components/SimpleModal/SimpleModal';
import './SchoolTeamForm.css';

interface Props {
  team?: any;
  onSubmit: (data) => void;
  onCancel: () => void;
  user: AppUser;
  forms: SchoolForm[];
  houses: SchoolHouse[];
}

const TeamFormUnloadGuard = ({ dirty }: { dirty: boolean }) => {
  React.useEffect(() => {
    if (!dirty) return undefined;
    const preventUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [dirty]);
  return null;
};

export const SchoolTeamForm: React.FunctionComponent<Props> = props => {
  const { team, forms, houses, user } = props;
  const requestModalClose = useSimpleModalRequestClose();
  const isTeamExist = typeof team !== 'undefined';

  const teamName = propz.get(team, ['name'], '');

  const SchoolTeamSchema = Yup.object().shape({
    name: Yup.string()
      .required('Required')
      .test('name', 'This team name has already been used for your chosen sport, please use a different name', function(
        value
      ) {
        const sportIds = propz.get(this.parent, ['sportIds'], []);
        const isExistSportId = sportIds.length > 0;
        const isTeamNameNotChanged = value === teamName;
        switch (true) {
          case !isExistSportId:
            return true;
          case !value:
            return false;
          case isTeamNameNotChanged:
            return true;
          default:
            return Promise.all(
              sportIds.map(sportId => checkAvailabilityTeamName(user, { name: value, sportId }))
            ).then(results => results.every(res => res.isAvailable));
        }
      }),
    sportIds: Yup.array()
      .min(1, 'Choose at least one sport')
      .required('Required'),
    season: Yup.string()
      .matches(/^\d{4}\/\d{2}$/, 'Use academic year format, for example 2026/27')
      .required('Required'),
    gender: Yup.string().required('Required'),
    houseId: Yup.string().when('isHouseFilter', {
      is: true,
      then: Yup.string().required('Choose a house')
    })
  });

  const renderGenderOptions = sport => {
    if (typeof sport === 'undefined') {
      return <option value={''}>Please select sport</option>;
    }

    let genderOptions = [];

    if (sport.genders.maleOnly) {
      genderOptions.push(
        <option key="gender_option_male" value={GENDER.MALE_ONLY}>
          {SPORT_GENDER_SERVER_TO_CLIENT_MAPPING.MALE_ONLY}
        </option>
      );
    }

    if (sport.genders.femaleOnly) {
      genderOptions.push(
        <option key="gender_option_female" value={GENDER.FEMALE_ONLY}>
          {SPORT_GENDER_SERVER_TO_CLIENT_MAPPING.FEMALE_ONLY}
        </option>
      );
    }

    if (sport.genders.mixed) {
      genderOptions.push(
        <option key="gender_option_mixed" value={GENDER.MIXED}>
          {SPORT_GENDER_SERVER_TO_CLIENT_MAPPING.MIXED}
        </option>
      );
    }

    return genderOptions;
  };

  const getAgeGroup = (): SelectOption[] => {
    const activeSchool = user.activeSchool;
    const ageGroupsNaming = activeSchool.ageGroupsNaming;

    return getSelectOptionForAge(forms, ageGroupsNaming);
  };

  const getSports = (text: string, selectedSportIds: string[] = []) => {
    const user = props.user;
    const isFavoriteSportsEnabled = propz.get(user, ['activeSchool', 'isFavoriteSportsEnabled'], false);

    return searchFunctionTeamSports(user, text, !isFavoriteSportsEnabled).then(sports =>
      sports.filter(sport => !selectedSportIds.some(id => String(id) === String(sport.id)))
    );
  };

  const getDefaultTeamSeason = (): string => {
    const now = new Date();
    const start = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
    return `${start}/${String(start + 1).slice(-2)}`;
  };

  const getSeasonOptions = (selectedSeason?: string): string[] => {
    const currentStart = Number(getDefaultTeamSeason().slice(0, 4));
    const seasons = Array.from({ length: 6 }, (_, index) => {
      const start = currentStart + 2 - index;
      return `${start}/${String(start + 1).slice(-2)}`;
    });
    if (selectedSeason && !seasons.includes(selectedSeason)) {
      seasons.push(selectedSeason);
    }
    return seasons.sort((left, right) => Number(right.slice(0, 4)) - Number(left.slice(0, 4)));
  };

  const teamInitial = {
    name: isTeamExist ? propz.get(team, ['name'], '') : '',
    description: isTeamExist ? propz.get(team, ['description'], '') : '',
    sports: propz.get(team, ['sports'], propz.get(team, ['sport']) ? [propz.get(team, ['sport'])] : []),
    sportIds: propz.get(team, ['sportIds'], propz.get(team, ['sportId']) ? [propz.get(team, ['sportId'])] : []),
    season: propz.get(team, ['season'], getDefaultTeamSeason()),
    ages: propz.get(team, ['ages'], []),
    gender: propz.get(team, ['gender'], ''),
    isHouseFilter: Boolean(propz.get(team, ['houseId'], false)),
    house: propz.get(team, ['house'], undefined),
    houseId: propz.get(team, ['houseId'], '')
  };

  const FormIcon = ({ name }: { name: 'check' | 'home' }) => (
    <svg
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={name === 'check' ? 'M20 6 9 17l-5-5' : 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'} />
    </svg>
  );

  return (
    <div className="teamEditor">
      <div className="teamEditor__intro">
        <strong>{isTeamExist ? teamName || 'Edit team' : 'Create a team'}</strong>
        <p>
          {isTeamExist ? 'Update this team’s details.' : 'Set up a squad you can pick for fixtures and tournaments.'}
        </p>
      </div>
      <div className="teamEditor__card">
        <div className="row">
          <div className="col-md-12">
            <Formik
              initialValues={teamInitial}
              validationSchema={SchoolTeamSchema}
              onSubmit={values => {
                props.onSubmit(values);
              }}
            >
              {({ touched, errors, setFieldValue, values, dirty }) => (
                <Form className="teamEditor__form">
                  <TeamFormUnloadGuard dirty={dirty} />
                  <label>Team name</label>
                  <Field type="text" name="name" className="form-control" placeholder="e.g. U14 Football A" />
                  {touched.name && errors.name ? <div className="teamEditor__error">{String(errors.name)}</div> : null}

                  <label>Description</label>
                  <Field
                    component="textarea"
                    name="description"
                    className="form-control"
                    placeholder="Add a short description"
                  />

                  <label>Sports</label>
                  <Field name="sport">
                    {({ field }) => {
                      return (
                        <Autocomplete
                          searchFunction={text => getSports(text, values.sportIds)}
                          getElementTitle={getName}
                          customClass="mFullWidth teamEditor__autocomplete"
                          isMenuPortal
                          menuMaxHeight={360}
                          key={values.sportIds.join(',')}
                          onSelect={sport => {
                            if (values.sportIds.includes(sport.id)) {
                              return;
                            }
                            setFieldValue('sportIds', [...values.sportIds, sport.id]);
                            setFieldValue('sports', [...values.sports, sport]);

                            const genderFieldByValue = {
                              [GENDER.MALE_ONLY]: 'maleOnly',
                              [GENDER.FEMALE_ONLY]: 'femaleOnly',
                              [GENDER.MIXED]: 'mixed'
                            };
                            if (values.gender && !sport.genders[genderFieldByValue[values.gender]]) {
                              setFieldValue('gender', '');
                            }

                            if (!values.gender) {
                              switch (true) {
                                case sport.genders.maleOnly:
                                  setFieldValue('gender', GENDER.MALE_ONLY);
                                  break;
                                case sport.genders.femaleOnly:
                                  setFieldValue('gender', GENDER.FEMALE_ONLY);
                                  break;
                                case sport.genders.mixed:
                                  setFieldValue('gender', GENDER.MIXED);
                                  break;
                              }
                            }
                          }}
                        />
                      );
                    }}
                  </Field>
                  <div className="teamEditor__chips">
                    {values.sports.map(sport => (
                      <button
                        key={sport.id}
                        type="button"
                        className="is-active"
                        onClick={() => {
                          setFieldValue(
                            'sportIds',
                            values.sportIds.filter(id => id !== sport.id)
                          );
                          setFieldValue(
                            'sports',
                            values.sports.filter(item => item.id !== sport.id)
                          );
                        }}
                      >
                        {sport.name} ×
                      </button>
                    ))}
                  </div>
                  {touched.sportIds && errors.sportIds ? (
                    <div className="teamEditor__error">{String(errors.sportIds)}</div>
                  ) : null}

                  <label>Season</label>
                  <Field component="select" name="season" className="form-control">
                    {getSeasonOptions(values.season).map(season => (
                      <option value={season} key={season}>
                        {season}
                      </option>
                    ))}
                  </Field>
                  {touched.season && errors.season ? (
                    <div className="teamEditor__error">{String(errors.season)}</div>
                  ) : null}

                  <label>
                    Gender <em>*</em>
                  </label>
                  <div className="teamEditor__chips">
                    {[
                      [
                        GENDER.MALE_ONLY,
                        SPORT_GENDER_SERVER_TO_CLIENT_MAPPING.MALE_ONLY,
                        values.sports.every(sport => sport.genders?.maleOnly)
                      ],
                      [
                        GENDER.FEMALE_ONLY,
                        SPORT_GENDER_SERVER_TO_CLIENT_MAPPING.FEMALE_ONLY,
                        values.sports.every(sport => sport.genders?.femaleOnly)
                      ],
                      [
                        GENDER.MIXED,
                        SPORT_GENDER_SERVER_TO_CLIENT_MAPPING.MIXED,
                        values.sports.every(sport => sport.genders?.mixed)
                      ]
                    ]
                      .filter(item => item[2])
                      .map(item => (
                        <button
                          key={String(item[0])}
                          type="button"
                          className={values.gender === item[0] ? 'is-active' : ''}
                          onClick={() => setFieldValue('gender', item[0])}
                        >
                          {values.gender === item[0] && <FormIcon name="check" />}
                          {item[1]}
                        </button>
                      ))}
                    {values.sports.length === 0 && (
                      <span className="teamEditor__emptyChoice">Choose at least one sport first</span>
                    )}
                  </div>
                  {touched.gender && errors.gender ? (
                    <div className="teamEditor__error">{String(errors.gender)}</div>
                  ) : null}

                  <label>Age groups</label>
                  <div className="teamEditor__chips">
                    {getAgeGroup().map(option => {
                      const active = values.ages.map(String).includes(String(option.value));
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={active ? 'is-active' : ''}
                          onClick={() =>
                            setFieldValue(
                              'ages',
                              active
                                ? values.ages.filter(age => String(age) !== String(option.value))
                                : [...values.ages, option.value]
                            )
                          }
                        >
                          {active && <FormIcon name="check" />}
                          {option.text}
                        </button>
                      );
                    })}
                  </div>
                  <small className="teamEditor__hint">Leave all unticked to open the team to every age group.</small>

                  {houses.length > 0 && (
                    <>
                      <div className="teamEditor__divider" />
                      <div className="teamEditor__houseSwitch">
                        <span className="teamEditor__homeIcon">
                          <FormIcon name="home" />
                        </span>
                        <div>
                          <strong>Filter by house</strong>
                          <small>Only draw players from a single house into this team.</small>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={values.isHouseFilter}
                          className={values.isHouseFilter ? 'is-active' : ''}
                          onClick={() => {
                            setFieldValue('isHouseFilter', !values.isHouseFilter);
                            if (values.isHouseFilter) {
                              setFieldValue('houseId', '');
                              setFieldValue('house', undefined);
                            }
                          }}
                        >
                          <span />
                        </button>
                      </div>

                      {values.isHouseFilter && (
                        <>
                          <PeopleFilterChipSelect
                            className="teamEditor__houseChips"
                            label="House"
                            options={houses.map(house => ({ value: String(house.id), label: house.name }))}
                            selected={values.houseId ? [String(values.houseId)] : []}
                            collapsedLimit={6}
                            onToggle={houseId => {
                              const isSelected = String(values.houseId) === String(houseId);
                              const house = houses.find(item => String(item.id) === String(houseId));
                              setFieldValue('houseId', isSelected ? '' : houseId);
                              setFieldValue('house', isSelected ? undefined : house);
                            }}
                          />
                          {touched.houseId && errors.houseId ? (
                            <div className="teamEditor__error">{String(errors.houseId)}</div>
                          ) : null}
                        </>
                      )}
                    </>
                  )}

                  <div className="teamEditor__actions">
                    <button
                      type="button"
                      className="teamEditor__cancel"
                      onClick={() => (requestModalClose ? requestModalClose() : props.onCancel())}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="teamEditor__save">
                      <FormIcon name="check" /> {isTeamExist ? 'Save changes' : 'Create team'}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

SchoolTeamForm.displayName = 'SchoolTeamForm';
